import React, { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect, useLocalSearchParams, usePathname, useSegments } from 'expo-router';
import { StyleSheet } from 'react-native';

import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { spacing } from 'styles/constants';
import { routes } from 'utils/routing';
import { parseBool } from 'utils/string-To-Bool';

import {
  RewardsTabLink,
  RewardsTabLinkText,
  RewardsTabsBorderBottom,
} from './loyalty-rewards.styled';

// TODO switch to https://github.com/EvanBacon/expo-router-top-tabs
export const LoyaltyRewardsTabs: React.FC = () => {
  const enableLoyaltyProfile = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROFILE);

  const pathname = usePathname();
  const { showSignUp: _showSignUp = true } = useLocalSearchParams<{ showSignUp: string }>();
  const showSignUp = parseBool(_showSignUp);

  const { loyaltyUser } = useLoyaltyUser();

  const historyTabKey = 'historyTab';

  const isIncentiveSelected = pathname && !!pathname.split('/')[3];
  const showCtaBar = !isIncentiveSelected;
  const showTabs = !!showCtaBar;

  const redeemTab = {
    label: t('redeemTab'),
    to: routes.rewardsList,
  };

  const historyTab = {
    label: t(historyTabKey),
    to: routes.rewardsHistory,
  };

  const ProfileTab = {
    label: t('profile'),
    to: routes.rewardsProfile,
  };

  const rewardsTabs = [redeemTab, ...(enableLoyaltyProfile ? [ProfileTab] : [historyTab])];

  const tabWidth = `${100 / rewardsTabs.length}%`;
  const {
    isAuthenticated,
    loading: isLoadingAuth,
    modalAuthIsOpen,
    openSignInModal,
  } = useAuthContext();
  const segments = useSegments();
  const hasAutoOpenedSignInRef = useRef(false);

  // Reset the one-shot prompt only when leaving Rewards for a real screen. Transparent
  // drawer overlays (hamburger / account, notification inbox) blur this tab but keep it
  // mounted — same latch semantics as useStoreRequired (BKPE-28088).
  useEffect(() => {
    const isDrawerOverlay = segments[0] === '(drawer)';
    const didNavigateAway = !isDrawerOverlay && !pathname?.startsWith(routes.rewardsList);

    if (didNavigateAway) {
      hasAutoOpenedSignInRef.current = false;
    }
  }, [pathname, segments]);

  useFocusEffect(
    useCallback(() => {
      if (!isAuthenticated && !isLoadingAuth && showSignUp && !modalAuthIsOpen) {
        // Dismissing auth or closing a drawer overlay re-focuses Rewards; without a latch
        // this would reopen the signup drawer on every return.
        if (hasAutoOpenedSignInRef.current) {
          return;
        }

        hasAutoOpenedSignInRef.current = true;
        openSignInModal({
          ...(pathname.includes(routes.loyaltyActivateQuest)
            ? {
                navigateOnSuccessRoute: routes.loyaltyActivateQuest,
              }
            : {}),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isLoadingAuth, showSignUp, openSignInModal, pathname])
  );

  if (!showTabs) {
    return false;
  }

  return (
    <CenteredRowContainer testID="rewards-tabs" contentFlexDirection="row">
      {rewardsTabs.map(({ label, to }) => {
        const isActiveTab = pathname === to;
        return (
          <RewardsTabLink
            replace
            key={label}
            linkPath={!loyaltyUser ? `${to}?showSignUp=false` : to}
            testID={`tabbed-reward-${label}`}
            active={isActiveTab}
            minWidth={tabWidth}
          >
            <RewardsTabLinkText
              variant="headerThree"
              active={isActiveTab}
              style={styles.tabLinkText}
            >
              {label}
            </RewardsTabLinkText>
          </RewardsTabLink>
        );
      })}
      <RewardsTabsBorderBottom />
    </CenteredRowContainer>
  );
};

const styles = StyleSheet.create({
  tabLinkText: {
    marginTop: spacing.$4,
    marginBottom: spacing.$4,
  },
});
